/**
 * 04/11/2026 (nuit) — « Indicateurs de résultats » (administrateur ; Qualiopi, indicateur 2 ; écarts E1, E4, E5, E15, E16).
 * Calculés à partir des données réelles de l'espace de formation, jamais saisis. « Publier les indicateurs » fige un
 * instantané daté que la vitrine peut lire (`/api/public/indicateurs`) ; rien n'est publié sans ce geste.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarRange, Clock, GraduationCap, LogOut, MessageSquareHeart, Send, Target, Users } from "lucide-react";
import { api, dateFr, instantFr, type VueIndicateurs } from "../api";
import { Alerte, Bouton, Carte, Chargement, Indicateur, Modale, TitrePage, useNotifier } from "../ui/base";

type Taux = VueIndicateurs["calcul"]["abandon"];

const valeurTaux = (t: Taux) => (t.statut === "publiable" ? `${t.taux} %` : t.statut === "effectif_insuffisant" ? "Effectif insuffisant" : "Aucune donnée");
const detailTaux = (t: Taux, unite: string, min: number) =>
  t.statut === "effectif_insuffisant" ? `${t.numerateur} / ${t.denominateur} ${unite} — publié à partir de ${min}` : t.denominateur ? `${t.numerateur} / ${t.denominateur} ${unite}` : `aucun ${unite.replace(/s$/, "")} sur la période`;
const nombre = (n: number) => new Intl.NumberFormat("fr-FR").format(n);

export function IndicateursResultats() {
  const anneeCourante = new Date().getFullYear();
  const [exercice, setExercice] = useState(anneeCourante - 1);
  const [confirmer, setConfirmer] = useState(false);
  const notifier = useNotifier();
  const requetes = useQueryClient();
  const vue = useQuery({ queryKey: ["indicateurs", exercice], queryFn: () => api.get<VueIndicateurs>(`/admin/indicateurs?exercice=${exercice}`) });
  const publier = useMutation({
    mutationFn: () => api.post(`/admin/indicateurs/publier?exercice=${exercice}`),
    onSuccess: async () => {
      setConfirmer(false);
      await requetes.invalidateQueries({ queryKey: ["indicateurs"] });
      notifier("succes", "Indicateurs publiés : l'instantané est figé et daté.");
    },
  });
  const seuil = useMutation({
    mutationFn: ({ id, valeur }: { id: string; valeur: number | null }) => api.put(`/admin/indicateurs/seuils/${id}`, { seuil: valeur }),
    onSuccess: () => requetes.invalidateQueries({ queryKey: ["indicateurs"] }),
  });
  if (vue.isPending) return <Chargement />;
  if (vue.error) return <Alerte ton="danger">{vue.error.message}</Alerte>;
  const { calcul: c, formations, publications, seuil_defaut } = vue.data;
  const min = c.effectif_minimal;
  const derniere = publications[0];

  return (
    <>
      <TitrePage
        titre="Indicateurs de résultats"
        soustitre={`Calculés à partir des dossiers, clôtures, évaluations et questionnaires de l'espace de formation (Qualiopi, indicateur 2). Période : du ${dateFr(c.periode.debut)} au ${dateFr(c.periode.fin)}, 12 mois glissants.`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-sm text-encre-2">
              Exercice BPF{" "}
              <select aria-label="Exercice BPF" value={exercice} onChange={(e) => setExercice(Number(e.target.value))} className="chiffres h-10 rounded-sm border border-trait-fort bg-carte px-3 text-sm">
                {[anneeCourante, anneeCourante - 1, anneeCourante - 2].map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </label>
            <Bouton variante="primaire" icone={<Send className="size-4" aria-hidden />} onClick={() => setConfirmer(true)}>
              Publier les indicateurs
            </Bouton>
          </div>
        }
      />

      <div className="mb-6">
        {derniere ? (
          <Alerte titre="Dernière publication">
            Publiée le {instantFr(derniere.publie_le)} (période du {dateFr(derniere.periode_debut)} au {dateFr(derniere.periode_fin)}, exercice BPF {derniere.exercice_bpf}). C'est elle que lit la vitrine, et non le calcul ci-dessous tant que vous ne republiez pas.
          </Alerte>
        ) : (
          <Alerte ton="attention" titre="Aucune publication à ce jour">
            Rien n'est encore visible publiquement. Un pourcentage n'est publié que si son effectif atteint {min} ; en dessous, la vitrine affichera « effectif insuffisant ».
          </Alerte>
        )}
      </div>

      <h2 className="mb-3 text-base font-semibold">Satisfaction</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Indicateur libelle="Satisfaction à chaud" valeur={valeurTaux(c.satisfaction.chaud.part_satisfaits)} detail={detailTaux(c.satisfaction.chaud.part_satisfaits, "répondants", min)} icone={<MessageSquareHeart className="size-4" aria-hidden />} ton="accent" />
        <Indicateur libelle="Taux de réponse à chaud" valeur={valeurTaux(c.satisfaction.chaud.taux_reponse)} detail={detailTaux(c.satisfaction.chaud.taux_reponse, "questionnaires", min)} icone={<Users className="size-4" aria-hidden />} />
        <Indicateur libelle="Satisfaction à froid" valeur={valeurTaux(c.satisfaction.froid.part_satisfaits)} detail={detailTaux(c.satisfaction.froid.part_satisfaits, "répondants", min)} icone={<MessageSquareHeart className="size-4" aria-hidden />} />
        <Indicateur libelle="Taux de réponse à froid" valeur={valeurTaux(c.satisfaction.froid.taux_reponse)} detail={detailTaux(c.satisfaction.froid.taux_reponse, "questionnaires", min)} icone={<Users className="size-4" aria-hidden />} />
      </div>

      <h2 className="mb-3 mt-8 text-base font-semibold">Résultats des parcours</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Indicateur libelle="Atteinte des objectifs" valeur={valeurTaux(c.atteinte_objectifs)} detail={detailTaux(c.atteinte_objectifs, "apprenants évalués", min)} icone={<Target className="size-4" aria-hidden />} ton="accent" />
        <Indicateur libelle="Taux d'abandon" valeur={valeurTaux(c.abandon)} detail={detailTaux(c.abandon, "apprenants", min)} icone={<LogOut className="size-4" aria-hidden />} />
        <Indicateur libelle={`Stagiaires formés (${c.activite.exercice})`} valeur={nombre(c.activite.nb_stagiaires)} detail={`${nombre(c.activite.heures_stagiaires)} heures-stagiaires, ${c.activite.nb_actions} action(s) — BPF`} icone={<GraduationCap className="size-4" aria-hidden />} />
        <Indicateur
          libelle="Traitement des réclamations"
          valeur={c.reclamations.delai_moyen_jours === null ? "—" : `${String(c.reclamations.delai_moyen_jours).replace(".", ",")} j`}
          detail={`délai moyen, ${c.reclamations.traitees} traitée(s) / ${c.reclamations.recues} reçue(s) sur 365 jours${c.reclamations.ouvertes ? `, ${c.reclamations.ouvertes} ouverte(s)` : ""}`}
          icone={<Clock className="size-4" aria-hidden />}
        />
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Carte className="p-5">
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <Target className="size-4 text-encre-3" aria-hidden /> Seuil de réussite par formation
          </h2>
          <p className="mt-0.5 text-[13px] text-encre-2">Score minimal à l'évaluation finale des acquis (07-FIN) pour compter l'objectif comme atteint. Vide : {seuil_defaut} %.</p>
          {formations.length === 0 ? (
            <p className="mt-3 text-sm text-encre-2">Aucune formation enregistrée.</p>
          ) : (
            <ul className="mt-3 divide-y divide-trait">
              {formations.map((f) => (
                <li key={f.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <span className="min-w-0 truncate">{f.titre || "Formation sans titre"}</span>
                  <span className="flex items-center gap-1">
                    <input
                      aria-label={`Seuil de réussite de ${f.titre}`}
                      inputMode="numeric"
                      defaultValue={f.seuil ?? ""}
                      placeholder={String(seuil_defaut)}
                      className="chiffres h-8 w-16 rounded-sm border border-trait-fort bg-carte px-2 text-right"
                      onBlur={(e) => {
                        const t = e.target.value.trim();
                        const valeur = t === "" ? null : Number(t);
                        if (valeur !== (f.seuil ?? null)) seuil.mutate({ id: f.id, valeur });
                      }}
                    />
                    %
                  </span>
                </li>
              ))}
            </ul>
          )}
          {seuil.error && <p className="mt-2 text-[13px] text-danger">{seuil.error.message}</p>}
        </Carte>

        <Carte className="p-5">
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <CalendarRange className="size-4 text-encre-3" aria-hidden /> Méthode de calcul
          </h2>
          <p className="mt-0.5 text-[13px] text-encre-2">{c.perimetre}</p>
          <ul className="mt-3 list-disc space-y-1.5 pl-5 text-[13px] text-encre-2">
            {c.methode.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
          <p className="mt-3 text-[13px] text-encre-3">Cette méthode est publiée avec chaque instantané.</p>
        </Carte>
      </div>

      {publications.length > 0 && (
        <Carte className="mt-6 p-5">
          <h2 className="text-base font-semibold">Historique des publications</h2>
          <ul className="mt-2 space-y-1 text-sm text-encre-2">
            {publications.map((p) => (
              <li key={p.id}>
                {instantFr(p.publie_le)} — période du {dateFr(p.periode_debut)} au {dateFr(p.periode_fin)}, exercice BPF {p.exercice_bpf}
              </li>
            ))}
          </ul>
        </Carte>
      )}

      <Modale ouverte={confirmer} fermer={() => setConfirmer(false)} titre="Publier les indicateurs ?">
        <p className="text-sm text-encre-2">
          Les valeurs affichées (période du {dateFr(c.periode.debut)} au {dateFr(c.periode.fin)}, exercice BPF {c.activite.exercice}) seront figées, datées et conservées comme preuve. La vitrine pourra les lire. Une nouvelle publication ne modifie pas les précédentes.
        </p>
        {publier.error && <div className="mt-3"><Alerte ton="danger">{publier.error.message}</Alerte></div>}
        <div className="mt-5 flex justify-end gap-2">
          <Bouton variante="discret" onClick={() => setConfirmer(false)}>
            Annuler
          </Bouton>
          <Bouton variante="primaire" enCours={publier.isPending} onClick={() => publier.mutate()}>
            Publier
          </Bouton>
        </div>
      </Modale>
    </>
  );
}
